import { useContext } from "react";
import { createFailure, ensure, validationFailed, ValidationResult, validationSuccessful } from "sprint-common-web";

import { UpdateSeasonRequest } from "../../../../api/UpdateSeasonRequest";
import { useAssetsManageTvSeriesApi } from "../../../../api/useAssetsManageTvSeriesApi";
import { TvSeasonDetails, TvSeriesDetails } from "../../../../TvSeriesDetails";
import { SeasonPageContext } from "../../context/SeasonPageContext";
import { UpdatedSeasonData } from "./EditSeasonForm";
import { EditSeasonFormInputs } from "./EditSeasonFormInputs";

export const useEditSeasonForm = (selectedSeasonId: string) => {
    const { selectedTvSeries, selectedSeason } = useContext(SeasonPageContext);

    const { updateSeason } = useAssetsManageTvSeriesApi();

    const defaultValues = getDefaultValues(selectedTvSeries, selectedSeasonId);

    const editSeason = async (inputs: EditSeasonFormInputs): Promise<UpdatedSeasonData> => {
        return updateSeason(selectedTvSeries.id, selectedSeasonId, createRequest(inputs)).then(() =>
            getUpdatedSeasonData(inputs)
        );
    };

    const getUpdatedSeasonData = (inputs: EditSeasonFormInputs): UpdatedSeasonData => {
        ensure(inputs.number);

        return {
            name: inputs.name,
            number: inputs.number,
            seasonId: selectedSeason.id
        };
    };

    const isNewSeasonNameUnique = (name: string): ValidationResult => {
        const seasons = selectedTvSeries.seasons;

        const isUnique = !seasons.some(
            (season: TvSeasonDetails) =>
                season.name.toLowerCase() === name?.toLowerCase() && season.id !== selectedSeason?.id
        );

        if (isUnique) {
            return validationSuccessful();
        }

        const failure = createFailure(
            "The season name is already taken. Please enter a different name",
            "season.TitleAlreadyTaken"
        );

        return validationFailed([failure]);
    };

    const isNewSeasonNumberUnique = (number: number): ValidationResult => {
        const isUnique = !selectedTvSeries.seasons.some(
            (season: TvSeasonDetails) =>
                season.number === number && selectedSeasonId !== season.id && !season.mergedTargetId
        );

        if (isUnique) {
            return validationSuccessful();
        }

        const failure = createFailure(
            "The season number is already taken. Please enter a different number",
            "season.NumberAlreadyTaken"
        );

        return validationFailed([failure]);
    };

    return {
        defaultValues,
        editSeason,
        isNewSeasonNameUnique,
        isNewSeasonNumberUnique
    };
};

const createRequest = (inputs: EditSeasonFormInputs): UpdateSeasonRequest => {
    ensure(inputs.number);

    return {
        name: inputs.name,
        number: inputs.number
    };
};

const getDefaultValues = (tvSeries: TvSeriesDetails, seasonId: string): EditSeasonFormInputs => {
    const defaultSeason = tvSeries.seasons.find((season: TvSeasonDetails) => season.id === seasonId);
    if (defaultSeason) {
        return {
            name: defaultSeason.name,
            number: defaultSeason.number
        };
    }

    return { name: "", number: null };
};
